import React, { useRef, useState } from 'react';

type EmailPreviewFrameProps = {
  html: string;
  title: string;
  className?: string;
  // 'auto' (default): the container grows tall enough to show the entire email scaled to fit
  //   the available width, with nothing cropped.
  // 'crop': the container keeps whatever fixed size its className gives it; the email is scaled
  //   to fit the width and anything past the container's height is cropped — for a small
  //   thumbnail/icon where only a glimpse of the top is needed.
  // 'contain': the container keeps whatever fixed size its className gives it; the email is
  //   scaled down to fit entirely inside on both axes and centered, nothing cropped — for
  //   several previews that need to look the same size regardless of each email's own height.
  fit?: 'auto' | 'crop' | 'contain';
};

// Fallback box size shown for the instant before the iframe has loaded and its real content
// size is known — arbitrary but close to a typical email's proportions to avoid a layout jump.
const FALLBACK_WIDTH = 700;
const FALLBACK_HEIGHT = 400;

// Renders an email's raw HTML at its natural size, then scales the whole iframe down to fit —
// see the `fit` prop for the three ways the container can relate to that scaled content. Measures
// actual content size on load since email heights vary.
export const EmailPreviewFrame = ({
  html,
  title,
  className,
  fit = 'auto',
}: EmailPreviewFrameProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [measured, setMeasured] = useState<{
    width: number;
    height: number;
    scale: number;
  } | null>(null);

  const handleLoad = () => {
    const doc = iframeRef.current?.contentDocument;
    const container = containerRef.current;
    if (!doc?.documentElement || !container) return;

    const width = doc.documentElement.scrollWidth;
    const height = doc.documentElement.scrollHeight;
    if (!width || !height) return;

    const widthScale = container.clientWidth / width;
    const scale =
      fit === 'contain'
        ? Math.min(widthScale, container.clientHeight / height)
        : widthScale;

    setMeasured({ width, height, scale });
  };

  return (
    <div
      ref={containerRef}
      className={className}
      style={{
        overflow: 'hidden',
        display: fit === 'contain' ? 'flex' : undefined,
        alignItems: fit === 'contain' ? 'center' : undefined,
        justifyContent: fit === 'contain' ? 'center' : undefined,
        height:
          fit === 'auto'
            ? measured
              ? measured.height * measured.scale
              : FALLBACK_HEIGHT
            : undefined,
      }}
    >
      <iframe
        ref={iframeRef}
        srcDoc={html}
        title={title}
        sandbox="allow-same-origin"
        onLoad={handleLoad}
        style={{
          width: measured ? measured.width : FALLBACK_WIDTH,
          height: measured ? measured.height : FALLBACK_HEIGHT,
          border: 0,
          flexShrink: 0,
          transform: measured ? `scale(${measured.scale})` : undefined,
          transformOrigin: fit === 'contain' ? 'center' : 'top left',
        }}
      />
    </div>
  );
};
